/**
 * Build the effective OCPP WebSocket URL from a base wsUrl + CPID.
 *
 * OCPP 1.6J convention (and the same path-segment shape carried into 2.0.1)
 * puts the ChargePointId at the END of the WebSocket URL as a path segment:
 *
 *   wss://csms.example/ocpp/CP01
 *                          └── this is the cpid the user configured
 *
 * The base wsUrl the user types (`wss://csms.example/ocpp` or
 * `wss://csms.example/ocpp/`) is the rendezvous endpoint published by the
 * CSMS. The CPID is added by us so the same CSMS endpoint can serve many
 * stations.
 *
 * Reference: legacy/evse2/js/ocpp_chargepoint.js — `new WebSocket(wsurl + "" + cpid, ["ocpp1.6"])`.
 * v3 cleans that up by normalising the trailing slash and URL-encoding the cpid.
 *
 * Examples:
 *   buildOcppWsUrl('wss://h/ocpp',  'CP01')     -> 'wss://h/ocpp/CP01'
 *   buildOcppWsUrl('wss://h/ocpp/', 'CP01')     -> 'wss://h/ocpp/CP01'
 *   buildOcppWsUrl('wss://h/',      'CP 01')    -> 'wss://h/CP%2001'
 *   buildOcppWsUrl('wss://h',       'CP01')     -> 'wss://h/CP01'
 *   buildOcppWsUrl('wss://h/ocpp',  'CP01/sub') -> 'wss://h/ocpp/CP01%2Fsub'
 *
 * If the user has ALREADY appended the cpid to wsUrl (e.g. pasted
 * `wss://csms.example/ocpp/CP01` and set CPID=CP01) we leave the URL alone
 * — appending again would produce `wss://csms.example/ocpp/CP01/CP01`.
 * The detection is conservative: we only skip the append when the last
 * path segment, URL-decoded, is exactly the cpid.
 *
 * Throws if wsUrl is not a valid ws:// or wss:// URL, or if cpid is empty.
 *
 * ⚠️  HTTP Basic Auth is intentionally NOT supported here.
 *
 * Session 14 added an optional `{username, password}` argument that
 * embedded credentials into the URL userinfo. The intent was OCPP
 * Security Profile 1. Live test against a real CPMS proved the
 * approach does not work in browsers: per the WHATWG WebSocket
 * standard, `new WebSocket('wss://user:pass@host/...')` silently
 * STRIPS the userinfo before the upgrade request — no
 * `Authorization` header is sent, even though the URL parses without
 * error. Reverted in commit c64f0b2.
 *
 * The browser WebSocket constructor accepts only `(url, protocols)`.
 * No way to attach Authorization (the API was deliberately closed by
 * the spec to prevent CSRF-style header forging on cross-origin WS
 * upgrades). If a future CSMS requires Basic Auth in a browser
 * context, the only viable paths are:
 *   1. CSMS-side support for credentials in `Sec-WebSocket-Protocol`
 *      (some OCPP gateways accept `['ocpp2.0.1', 'Basic.<b64>']`).
 *   2. Cookie pre-flight via `fetch()` if CSMS issues Set-Cookie.
 *   3. Local Node WS proxy that adds the header and forwards.
 *
 * Do NOT re-introduce userinfo injection here without verifying the
 * target browser actually sends the resulting Authorization header
 * (DevTools → Network → WS request → Headers section). Postman
 * "working" is not evidence — it has its own HTTP client.
 */
export function buildOcppWsUrl(wsUrl: string, cpid: string): string {
  if (typeof wsUrl !== 'string' || wsUrl.length === 0) {
    throw new Error('buildOcppWsUrl: wsUrl is required');
  }
  if (typeof cpid !== 'string' || cpid.length === 0) {
    throw new Error('buildOcppWsUrl: cpid is required');
  }
  let parsed: URL;
  try {
    parsed = new URL(wsUrl);
  } catch {
    throw new Error(`buildOcppWsUrl: not a valid URL: ${wsUrl}`);
  }
  if (parsed.protocol !== 'ws:' && parsed.protocol !== 'wss:') {
    throw new Error(`buildOcppWsUrl: expected ws:// or wss://, got ${parsed.protocol} (${wsUrl})`);
  }

  // If the last non-empty path segment already equals the cpid (URL-decoded),
  // assume the user already appended it — return wsUrl unchanged (sans any
  // trailing slash for consistency).
  const segments = parsed.pathname.split('/').filter((s) => s.length > 0);
  const lastSegment = segments[segments.length - 1];
  if (lastSegment !== undefined) {
    let decoded: string;
    try {
      decoded = decodeURIComponent(lastSegment);
    } catch {
      decoded = lastSegment;
    }
    if (decoded === cpid) {
      // Strip a trailing slash from the pathname for consistency, but keep
      // any query/fragment exactly where the URL parser placed them.
      parsed.pathname = parsed.pathname.replace(/\/+$/, '') || '/';
      return parsed.toString();
    }
  }

  // Append cpid as a path segment. Mutating `parsed.pathname` keeps the
  // query string and fragment in their canonical positions (after the
  // pathname); naive string concatenation would inject the cpid mid-URL.
  const trimmed = parsed.pathname.replace(/\/+$/, '');
  parsed.pathname = `${trimmed}/${encodeURIComponent(cpid)}`;
  return parsed.toString();
}
